import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, getCategoryColor, getStatusColor, getTagColor, getModuleRoute } from '../utils/constants';
import supabase from '../lib/supabase';

const { 
  FiPlus, FiHelpCircle, FiBookOpen, FiTrendingUp, FiUsers, FiDatabase, 
  FiUser, FiGrid, FiClipboard, FiShield, FiFileText, FiShoppingCart, 
  FiTool, FiMessageSquare, FiLoader, FiAlertTriangle, FiLogOut, FiInfo
} = FiIcons;

const Dashboard = ({ onNavigate, user, onSignOut }) => {
  const [latestQuestions, setLatestQuestions] = useState([]);
  const [latestLearnings, setLatestLearnings] = useState([]);
  const [stats, setStats] = useState({
    totalQuestions: 0,
    totalLearnings: 0,
    totalFeedback: 0,
    totalAnswers: 0,
    openQuestions: 0,
    growthRate: '+22%'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch latest 5 questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('questions_kb2024')
        .select('id, title, category, status, tags, asked_by, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (questionsError) throw questionsError;

      // Fetch latest 5 learnings
      const { data: learningsData, error: learningsError } = await supabase
        .from('learnings_kb2024')
        .select('id, title, category, tags, shared_by, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      if (learningsError) throw learningsError;

      // Fetch stats
      const [questionsCount, learningsCount, feedbackCount, answersCount, openQuestionsCount] = await Promise.all([
        supabase.from('questions_kb2024').select('id', { count: 'exact', head: true }),
        supabase.from('learnings_kb2024').select('id', { count: 'exact', head: true }),
        supabase.from('feedback_kb2024').select('id', { count: 'exact', head: true }),
        supabase.from('answers_kb2024').select('id', { count: 'exact', head: true }).then(result => 
          result.error && result.error.code === 'PGRST116' ? { count: 0 } : result
        ),
        supabase.from('questions_kb2024').select('id', { count: 'exact', head: true }).eq('status', 'Open')
      ]);

      setLatestQuestions(questionsData || []);
      setLatestLearnings(learningsData || []);
      setStats({
        totalQuestions: questionsCount.count || 0,
        totalLearnings: learningsCount.count || 0,
        totalFeedback: feedbackCount.count || 0,
        totalAnswers: answersCount.count || 0,
        openQuestions: openQuestionsCount.count || 0,
        growthRate: '+22%'
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Unable to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      onSignOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleOpenQuestionsClick = () => {
    // Navigate to questions page with Open status filter
    onNavigate('questions-filtered', { status: 'Open' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Certification': FiShield,
      'QDMS': FiDatabase,
      'QHSE': FiShield,
      'Forms and Checklists': FiFileText,
      'Procurement': FiShoppingCart,
      'Maintenance': FiTool,
      'General': FiGrid
    };
    return icons[category] || FiGrid;
  };

  const getUserRole = () => {
    return user?.user_metadata?.user_role || 'User';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Super User': 'bg-purple-100 text-purple-800',
      'Maersk Team': 'bg-blue-100 text-blue-800',
      'Mariapps SME': 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <div className="text-red-600 mb-4">
            <SafeIcon icon={FiAlertTriangle} className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}!
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
              <p className="text-gray-600">Manage your questions and share your learnings across all modules</p>
              <span className={`px-3 py-1 text-sm font-medium rounded-full self-start ${getRoleColor(getUserRole())}`}>
                {getUserRole()}
              </span>
            </div>
          </div>
          
          {/* Header Action Buttons - Responsive Layout */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:flex-row">
            <button
              onClick={() => onNavigate('quick-start-guide')}
              className="flex items-center justify-center sm:justify-start gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
            >
              <SafeIcon icon={FiInfo} className="w-4 h-4" />
              <span className="whitespace-nowrap">Quick Start Guide</span>
            </button>
            <button
              onClick={() => onNavigate('profile')}
              className="flex items-center justify-center sm:justify-start gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
            >
              <SafeIcon icon={FiUser} className="w-4 h-4" />
              <span className="whitespace-nowrap">Profile</span>
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center justify-center sm:justify-start gap-2 bg-red-100 hover:bg-red-200 text-red-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              <span className="whitespace-nowrap">Sign Out</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8"
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Questions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalQuestions}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiHelpCircle} className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Answers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAnswers}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Learnings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalLearnings}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Feedback</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalFeedback}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiClipboard} className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <button
          onClick={handleOpenQuestionsClick}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-red-200 hover:bg-red-50 transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-sm font-medium text-gray-600 group-hover:text-red-700">Open Questions</p>
              <p className="text-2xl font-bold text-gray-900 group-hover:text-red-900">{stats.openQuestions}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200">
              <SafeIcon icon={FiHelpCircle} className="w-6 h-6 text-red-600 group-hover:text-red-700" />
            </div>
          </div>
        </button>
      </motion.div>

      {/* Module Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-8"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Modules</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {CATEGORIES.map((category, index) => {
            const IconComponent = getCategoryIcon(category);
            return (
              <motion.button
                key={category}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                onClick={() => onNavigate(getModuleRoute(category))}
                className="flex flex-col items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all duration-200"
              >
                <SafeIcon icon={IconComponent} className="w-8 h-8 text-blue-600" />
                <span className="font-medium text-gray-900 text-sm text-center">{category}</span>
              </motion.button>
            );
          })}
        </div>
      </motion.div>

      {/* Quick Navigation Menu */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8"
      >
        <button
          onClick={() => onNavigate('questions')}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all duration-200"
        >
          <SafeIcon icon={FiHelpCircle} className="w-6 h-6 text-blue-600" />
          <span className="font-medium text-gray-900">All Questions</span>
        </button>

        <button
          onClick={() => onNavigate('learnings')}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-all duration-200"
        >
          <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-green-600" />
          <span className="font-medium text-gray-900">All Learnings</span>
        </button>

        <button
          onClick={() => onNavigate('feedback')}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 hover:border-orange-200 hover:bg-orange-50 transition-all duration-200"
        >
          <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-orange-600" />
          <span className="font-medium text-gray-900">All Feedback</span>
        </button>

        <button
          onClick={() => onNavigate('knowledge-base')}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 hover:border-purple-200 hover:bg-purple-50 transition-all duration-200"
        >
          <SafeIcon icon={FiDatabase} className="w-6 h-6 text-purple-600" />
          <span className="font-medium text-gray-900">Knowledge Base</span>
        </button>

        <button
          onClick={() => onNavigate('profile')}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all duration-200"
        >
          <SafeIcon icon={FiUser} className="w-6 h-6 text-gray-600" />
          <span className="font-medium text-gray-900">My Profile</span>
        </button>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="flex flex-col sm:flex-row gap-4 mb-8"
      >
        <button
          onClick={() => onNavigate('question-form')}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Ask a Question
        </button>

        <button
          onClick={() => onNavigate('learning-form')}
          className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Share a Learning
        </button>

        <button
          onClick={() => onNavigate('feedback-form')}
          className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Submit Feedback
        </button>
      </motion.div>

      {/* Content Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Latest Questions Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Latest Questions</h2>
            <button
              onClick={() => onNavigate('questions')}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {latestQuestions.length > 0 ? (
                latestQuestions.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors duration-200 cursor-pointer"
                    onClick={() => onNavigate('question-details', question.id)}
                  >
                    <h3 className="font-medium text-gray-900 mb-3">{question.title}</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(question.category)}`}>
                        {question.category}
                      </span>
                      {question.tags && question.tags.length > 0 && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(question.tags[0])}`}>
                          {question.tags[0]}
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(question.status)}`}>
                        {question.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Asked by {question.asked_by} • {formatDate(question.created_at)}
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiHelpCircle} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No questions yet</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Latest Learnings Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Latest Learnings</h2>
            <button
              onClick={() => onNavigate('learnings')}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {latestLearnings.length > 0 ? (
                latestLearnings.map((learning, index) => (
                  <motion.div
                    key={learning.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors duration-200 cursor-pointer"
                    onClick={() => onNavigate('learning-details', learning.id)}
                  >
                    <h3 className="font-medium text-gray-900 mb-3">{learning.title}</h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(learning.category)}`}>
                        {learning.category}
                      </span>
                      {learning.tags && learning.tags.length > 0 && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(learning.tags[0])}`}>
                          {learning.tags[0]}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      Shared by {learning.shared_by} • {formatDate(learning.created_at)}
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiBookOpen} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No learnings yet</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;